<template>
  <!-- Modal para cambiar la contraseña de un usuario -->
  <div v-if="showChangePasswordModal" class="modal-container">
    <div class="modal-content">
      <div class="modal-header red-header">
        <h5 class="modal-title">
          {{ $t('cambiar_contrasena') }}<br />
          <span class="user-name">{{ selectedUserName }}</span>
        </h5>
        <button 
          type="button" 
          class="btn-close" 
          @click="closeChangePasswordModal" 
          @keydown.enter="closeChangePasswordModal"
          @keydown.space="closeChangePasswordModal"
          aria-label="Cerrar modal"
        >
          ×
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="handleChangePassword">
          <div class="mb-3">
            <label for="new_password" class="form-label">{{ $t('nueva_contrasena') }}</label>
            <div class="password-container">
              <input
                :type="showNewPassword ? 'text' : 'password'"
                id="new_password"
                v-model="passwordForm.new_password"
                class="form-input"
                required
                autocomplete="new-password"
              />
              <span
                class="material-icons password-toggle"
                role="button"
                tabindex="0"
                @click="toggleShowNewPassword"
                @keydown.enter.prevent="toggleShowNewPassword"
                @keydown.space.prevent="toggleShowNewPassword"
                :aria-label="showNewPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'"
              >
                {{ showNewPassword ? 'visibility_off' : 'visibility' }}
              </span>
            </div>
          </div>
          <div class="mb-3">
            <label for="new_password_confirmation" class="form-label">{{ $t('repetir_contraseña') }}</label>
            <div class="password-container">
              <input
                :type="showConfirmPassword ? 'text' : 'password'"
                id="new_password_confirmation"
                v-model="passwordForm.new_password_confirmation"
                class="form-input"
                required
                autocomplete="new-password"
              />
              <span
                class="material-icons password-toggle"
                role="button"
                tabindex="0"
                @click="toggleShowConfirmPassword"
                @keydown.enter.prevent="toggleShowConfirmPassword"
                @keydown.space.prevent="toggleShowConfirmPassword"
                :aria-label="showConfirmPassword ? 'Ocultar confirmación' : 'Mostrar confirmación'"
              >
                {{ showConfirmPassword ? 'visibility_off' : 'visibility' }}
              </span>
            </div>
          </div>
          <div class="form-check">
            <input
              type="checkbox"
              id="forzar_cambio_contrasena"
              v-model="passwordForm.forzar_cambio_contrasena"
              class="form-check-input"
            />
            <label class="form-check-label" for="forzar_cambio_contrasena">
              {{ $t('forzar_cambio_contrasena_al_iniciar') }}
            </label>
          </div>
          <div class="modal-footer">
            <div class="form-actions">
              <button type="submit" class="btn btn-primary">{{ $t('guardar') }}</button>
              <button 
                type="button" 
                class="btn-cancel" 
                @click="closeChangePasswordModal"
                @keydown.enter="closeChangePasswordModal"
                @keydown.space="closeChangePasswordModal"
              >
                {{ $t('cancelar') }}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>

  <div class="welcome-container">
    <!-- Título y botón para agregar usuarios -->
    <div class="title-container">
      <h1 class="title">Usuarios</h1>
    </div>

    <div class="table-header">
      <div class="add-user-search-row">
        <button 
          class="btn btn-add-user" 
          @click="openModal(false)" 
          @keydown.enter="openModal(false)"
          @keydown.space="openModal(false)"
          :title="$t('agregar_usuario')"
          :aria-label="$t('agregar_usuario')"
        >
          {{ $t('agregar_usuario') }}
        </button>
        <button
          class="btn btn-logs"
          @click="viewAllLogs"
          @keydown.enter.prevent="viewAllLogs"
          @keydown.space.prevent="viewAllLogs"
          :title="$t('ver_logs_todos_usuarios')"
          :aria-label="$t('ver_logs_todos_usuarios')"
          style="margin-left:8px;"
        >
          {{ $t('ver_logs_todos_usuarios') }}
        </button>
        <button
          class="btn btn-logs"
          @click="viewAllFichajes"
          @keydown.enter.prevent="viewAllFichajes"
          @keydown.space.prevent="viewAllFichajes"
          :title="$t('ver_fichajes')"
          :aria-label="$t('ver_fichajes')"
          style="margin-left:8px;"
        >
          {{ $t('ver_fichajes') }}
        </button>
        <input
          type="text"
          v-model="searchNombreCompleto"
          placeholder="Buscar por nombre completo, usuario o email"
          class="form-control"
          style="max-width: 250px;"
          aria-label="Buscar usuarios por nombre, usuario o correo electrónico"
        />
      </div>
    </div>

    <!-- Contenedor de la tabla -->
    <div class="table-container">
      <div v-if="loadingUsers" class="loading-state">
        <span class="material-icons spin">refresh</span>
        Cargando usuarios...
      </div>
      <table class="user-table" v-else-if="filteredUsers.length > 0">
        <thead>
          <tr>
            <th>
              {{ $t('nombre_completo') }}
              <span class="th-sort-control" role="button" tabindex="0" @click="setSort('nombre_completo')" style="margin-left:6px; font-size:0.9em; cursor:pointer;"> <span v-if="sortBy==='nombre_completo'">{{ sortDir==='asc' ? '▲' : '▼' }}</span><span v-else>⇅</span></span>
            </th>
            <th>
              {{ $t('nombre_usuario') }}
              <span class="th-sort-control" role="button" tabindex="0" @click="setSort('nombre_usuario')" style="margin-left:6px; font-size:0.9em; cursor:pointer;"> <span v-if="sortBy==='nombre_usuario'">{{ sortDir==='asc' ? '▲' : '▼' }}</span><span v-else>⇅</span></span>
            </th>
            <th>
              {{ $t('email') }}
              <span class="th-sort-control" role="button" tabindex="0" @click="setSort('email')" style="margin-left:6px; font-size:0.9em; cursor:pointer;"> <span v-if="sortBy==='email'">{{ sortDir==='asc' ? '▲' : '▼' }}</span><span v-else>⇅</span></span>
            </th>
            <th>
              {{ $t('rol') }}
              <span class="th-sort-control" role="button" tabindex="0" @click="setSort('rol')" style="margin-left:6px; font-size:0.9em; cursor:pointer;"> <span v-if="sortBy==='rol'">{{ sortDir==='asc' ? '▲' : '▼' }}</span><span v-else>⇅</span></span>
            </th>
            <th>
              {{ $t('delegacion') }}
              <span class="th-sort-control" role="button" tabindex="0" @click="setSort('delegacion')" style="margin-left:6px; font-size:0.9em; cursor:pointer;"> <span v-if="sortBy==='delegacion'">{{ sortDir==='asc' ? '▲' : '▼' }}</span><span v-else>⇅</span></span>
            </th>
            <th>
              {{ $t('departamento') }}
              <span class="th-sort-control" role="button" tabindex="0" @click="setSort('departamento')" style="margin-left:6px; font-size:0.9em; cursor:pointer;"> <span v-if="sortBy==='departamento'">{{ sortDir==='asc' ? '▲' : '▼' }}</span><span v-else>⇅</span></span>
            </th>
            <th>
              {{ $t('usuario_responsable') }}
              <span class="th-sort-control" role="button" tabindex="0" @click="setSort('usuario_responsable')" style="margin-left:6px; font-size:0.9em; cursor:pointer;"> <span v-if="sortBy==='usuario_responsable'">{{ sortDir==='asc' ? '▲' : '▼' }}</span><span v-else>⇅</span></span>
            </th>
            <th>
              {{ $t('bloqueado') }}
              <span class="th-sort-control" role="button" tabindex="0" @click="setSort('bloqueado')" style="margin-left:6px; font-size:0.9em; cursor:pointer;"> <span v-if="sortBy==='bloqueado'">{{ sortDir==='asc' ? '▲' : '▼' }}</span><span v-else>⇅</span></span>
            </th>
            <th>{{ $t('acciones') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="user in filteredUsers"
            :key="user.id"
            @dblclick="openModal(true, user)"
            :class="{ 'self-row': isSelf(user) }"
          >
            <td :title="user.nombre_completo">
              {{ user.nombre_completo }}
              <span v-if="isSelf(user)" class="self-badge" title="Usuario logueado" aria-label="Usuario logueado">YO</span>
            </td>
            <td>{{ user.nombre_usuario }}</td>
            <td>{{ user.email }}</td>
            <td>{{ user.rol ? user.rol.nombre : 'Sin rol' }}</td>
            <td>{{ user.delegacion ? user.delegacion.nombre : 'Sin delegación' }}</td>
            <td>{{ user.departamento ? user.departamento.nombre : 'Sin departamento' }}</td>
            <td>
              <span v-if="user.usuario_responsable">{{ user.usuario_responsable.nombre_completo }}</span>
              <span v-else class="empty-cell">-</span>
            </td>
            <td class="bloqueado-column" :title="user.bloqueado ? 'Bloqueado' : 'Activo'">
              <span v-if="user.bloqueado === 1 || user.bloqueado === true" class="material-icons bloqueado-icon" aria-hidden="true">check_circle</span>
              <span v-else class="empty-cell">-</span>
            </td>
            <td class="actions icon-row-horizontal">
              <!-- EDITAR (permitido para el propio usuario) -->
              <span 
                class="material-icons action-icon" 
                role="button"
                tabindex="0"
                @click="handleIconAction($event, () => openModal(true, user), user, true)"
                @keydown.enter.prevent="handleIconAction($event, () => openModal(true, user), user, true)"
                @keydown.space.prevent="handleIconAction($event, () => openModal(true, user), user, true)"
                :aria-label="$t('editar') + ' ' + user.nombre_completo"
                :title="$t('editar')"
              >
                edit
              </span>
 
               <!-- CAMBIAR CONTRASEÑA -->
              <span 
                class="material-icons action-icon" 
                role="button"
                tabindex="0"
                @click="handleIconAction($event, () => openChangePasswordModal(user.id), user, true)"
                @keydown.enter.prevent="handleIconAction($event, () => openChangePasswordModal(user.id), user, true)"
                @keydown.space.prevent="handleIconAction($event, () => openChangePasswordModal(user.id), user, true)"
                :aria-label="$t('cambiar_contrasena') + ' ' + user.nombre_completo"
                :title="$t('cambiar_contrasena')"
              >
                vpn_key
              </span>

              <!-- VER LOGS -->
              <span 
                class="material-icons action-icon" 
                role="button"
                tabindex="0"
                @click="viewLogs(user.id)"
                @keydown.enter="viewLogs(user.id)"
                @keydown.space="viewLogs(user.id)"
                :aria-label="$t('ver_logs') + ' ' + user.nombre_completo"
                :title="$t('ver_logs')"
              >
                history
              </span>

              <!-- VER FICHAJES (nuevo) -->
              <span 
                class="material-icons action-icon" 
                role="button"
                tabindex="0"
                @click="viewUserFichajes(user.id)"
                @keydown.enter="viewUserFichajes(user.id)"
                @keydown.space="viewUserFichajes(user.id)"
                :aria-label="$t('ver_fichajes') + ' ' + user.nombre_completo"
                :title="$t('ver_fichajes')"
                style="margin-left:6px;"
              >
                schedule
              </span>

              <!-- BLOQUEAR/DESBLOQUEAR -->
              <span 
                v-if="user.bloqueado"
                class="material-icons action-icon unlock-icon"
                role="button"
                tabindex="0"
                @click="handleIconAction($event, () => toggleUserBlock(user), user)"
                @keydown.enter.prevent="handleIconAction($event, () => toggleUserBlock(user), user)"
                @keydown.space.prevent="handleIconAction($event, () => toggleUserBlock(user), user)"
                :aria-label="$t('desbloquear_usuario') + ' ' + user.nombre_completo"
                :aria-disabled="isSelf(user)"
                :title="isSelf(user) ? $t('no_puede_desbloquearse') : $t('desbloquear_usuario')"
                :class="{ 'disabled-action': isSelf(user) }"
              >
                lock_open
              </span>
              <span 
                v-else
                class="material-icons action-icon lock-icon"
                role="button"
                tabindex="0"
                @click="handleIconAction($event, () => toggleUserBlock(user), user)"
                @keydown.enter.prevent="handleIconAction($event, () => toggleUserBlock(user), user)"
                @keydown.space.prevent="handleIconAction($event, () => toggleUserBlock(user), user)"
                :aria-label="$t('bloquear_usuario') + ' ' + user.nombre_completo"
                :aria-disabled="isSelf(user)"
                :title="isSelf(user) ? $t('no_puede_bloquearse') : $t('bloquear_usuario')"
                :class="{ 'disabled-action': isSelf(user) }"
              >
                lock
              </span>

              <!-- ELIMINAR -->
              <span 
                class="material-icons action-icon delete-icon"
                role="button"
                tabindex="0"
                @click.stop="handleIconAction($event, () => deleteUser(user.id), user)"
                @keydown.enter.prevent.stop="handleIconAction($event, () => deleteUser(user.id), user)"
                @keydown.space.prevent.stop="handleIconAction($event, () => deleteUser(user.id), user)"
                :aria-label="$t('eliminar_usuario') + ' ' + user.nombre_completo"
                :aria-disabled="isSelf(user)"
                :title="isSelf(user) ? $t('no_puede_eliminarse') : $t('eliminar_usuario')"
                :class="{ 'disabled-action': isSelf(user) }"
              >
                delete
              </span>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-else class="empty-state">
        <span class="material-icons action-icon">group_off</span>
        {{ $t('no_usuarios_registrados') }}
      </div>
    </div>

    <!-- Reutilizar componente UserLogModal para mostrar logs -->
    <UserLogModal
      :visible="showUserLogsModal"
      :logs="userLogs"
      :title="selectedUserName || $t('logs_todos_usuarios')"
      @close="closeUserLogsModal"
    />

    <UserFichajesModal
      v-if="showFichajesModal"
      v-model:visible="showFichajesModal"
      :fichajes="fichajesForModal"
      :title="$t('ver_fichajes')"
      :fixedUserId="fichajesModalUserId"
      @update:visible="onFichajesModalVisibleChange"
    />

    <UserFormView
      v-if="showUserForm"
      :isEditMode="isEditMode"
      :form="form"
      @close="showUserForm = false"
      @refreshTable="loadUsers"
    />
  </div>
</template>

<script>
import 'bootstrap/dist/css/bootstrap.min.css';
import { Modal } from 'bootstrap';
import UserFormView from './UserFormView.vue';
import Header from '../components/Header.vue';
import UserLogModal from '@/views/UserLogModal.vue';
import UserFichajesModal from '@/views/UserFichajesModalView.vue';
import api from '../api';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';

export default {
  name: 'UsersView',
  components: {
    Header,
    UserFormView,
  UserLogModal,
  UserFichajesModal,
  },
  data() {
    return {
      // id del usuario logueado (usar localStorage si el login lo guarda)
      loadingUsers: false,
      userName: 'Cargando...',
      users: [],
      searchNombreCompleto: '',
      roles: [],
      usuariosResponsables: [],
      isEditMode: false,
      form: {
        nombre_completo: '',
        nombre_usuario: '',
        email: '',
        password: '',
        confirm_password: '',
        rol_id: null,
        usuario_responsable_id: null,
      },
      logs: [],
      userLogs: [],
      filteredLogs: [],
      filterDate: '',
      selectedUserName: '',
      selectedUserId: null,
      showUserForm: false,
      showUserLogsModal: false,
      showChangePasswordModal: false,
    showFichajesModal: false,
    fichajesForModal: [],
    fichajesModalUserId: null,
    showNewPassword: false,
    showConfirmPassword: false,
    // Sorting for users table
    sortBy: null,
    sortDir: 'asc',
      passwordForm: {
        new_password: '',
        new_password_confirmation: '',
        forzar_cambio_contrasena: true,
      },
    };
  },
  computed: {
    filteredUsers() {
      // Si no hay texto de búsqueda devolvemos todos los usuarios
      let res = [];
      if (!this.searchNombreCompleto) res = [...this.users];
      else {
        const search = this.searchNombreCompleto.trim().toLowerCase();
        if (!search) res = [...this.users];
        else {
          res = this.users.filter(user => {
            const nombre = (user.nombre_completo || '').toString().toLowerCase();
            const usuario = (user.nombre_usuario || '').toString().toLowerCase();
            const email = (user.email || '').toString().toLowerCase();

            return (
              nombre.includes(search) ||
              usuario.includes(search) ||
              email.includes(search)
            );
          });
        }
      }

      // Apply sorting if requested
      if (this.sortBy) {
        const col = this.sortBy;
        const dir = this.sortDir === 'asc' ? 1 : -1;
        res.sort((a, b) => {
          let va = '';
          let vb = '';

          if (col === 'rol') {
            va = a.rol?.nombre || '';
            vb = b.rol?.nombre || '';
          } else if (col === 'delegacion') {
            va = a.delegacion?.nombre || '';
            vb = b.delegacion?.nombre || '';
          } else if (col === 'departamento') {
            va = a.departamento?.nombre || '';
            vb = b.departamento?.nombre || '';
          } else if (col === 'usuario_responsable') {
            va = a.usuario_responsable?.nombre_completo || '';
            vb = b.usuario_responsable?.nombre_completo || '';
          } else if (col === 'bloqueado') {
            const na = (a.bloqueado === true || a.bloqueado === 1 || a.bloqueado === '1') ? 1 : 0;
            const nb = (b.bloqueado === true || b.bloqueado === 1 || b.bloqueado === '1') ? 1 : 0;
            return dir * (na - nb);
          } else {
            va = (a[col] === undefined || a[col] === null) ? '' : a[col];
            vb = (b[col] === undefined || b[col] === null) ? '' : b[col];
          }

          // Comparación por cadena para todos los casos no-bloqueado
          return dir * String(va).toLowerCase().localeCompare(String(vb).toLowerCase(), undefined, { numeric: true, sensitivity: 'base' });
        });
      }

      return res;
    },
    // ✅ Computed para localStorage seguro
    currentUserId() {
      try {
        const raw = sessionStorage.getItem('user_id');
        if (!raw || raw === 'null' || raw === 'undefined' || raw === '') return null;
        const num = Number(raw);
        return Number.isNaN(num) ? null : num;
      } catch {
        return null;
      }
    },
  },
  methods: {
    // ✅ Helper: determinar si la fila corresponde al usuario logueado
    isSelf(user) {
      if (!user || !this.currentUserId) return false;
      return Number(user.id) === Number(this.currentUserId);
    },

    // ✅ Método para iconos accesibles
    handleIconAction(event, action, user = null, allowIfSelf = false) {
      // Si la acción no permite que el propio usuario la ejecute, bloquearla
      if (user && this.isSelf(user) && !allowIfSelf) return;

      // Manejar teclado (Enter/Space) o click
      if (event.type === 'keydown' && !['Enter', ' ', 'Spacebar'].includes(event.key)) {
        return;
      }

      event.preventDefault();
      action();
    },
    
    clearDateFilter() {
      this.filterDate = '';
      this.filteredLogs = this.userLogs;
    },
    
    exportToExcel() {
      try {
        const header = [
          this.$t('id'),
          this.$t('usuario'),
          this.$t('inicio_sesion'),
          this.$t('fin_sesion'),
          this.$t('expulsado'),
          this.$t('motivo_fallo')
        ];
        const data = this.filteredLogs.map(log => [
          log.id,
          log.nombre_usuario || this.$t('desconocido'),
          log.inicio_sesion,
          log.fin_sesion || this.$t('en_sesion'),
          log.expulsado === 1 ? '✔️' : '',
          log.motivo_fallo || ''
        ]);
        const allRows = [header, ...data];
        const ws = XLSX.utils.aoa_to_sheet(allRows);

        // Calcular el ancho máximo de cada columna
        const colWidths = header.map((_, colIdx) => {
          return {
            wch: Math.max(
              ...allRows.map(row => (row[colIdx] ? String(row[colIdx]).length : 0)),
              10 // ancho mínimo
            )
          };
        });
        ws['!cols'] = colWidths;

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Logs");
        XLSX.writeFile(wb, `logs_${this.selectedUserName}_${new Date().toISOString().split('T')[0]}.xlsx`);
        
        Swal.fire({
          icon: 'success',
          title: 'Exportado',
          text: 'Los logs se han exportado correctamente',
          timer: 1500,
          showConfirmButton: false
        });
      } catch (error) {
        console.error('Error exportando logs:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo exportar el archivo'
        });
      }
    },
    
    openChangePasswordModal(userId) {
      const user = this.users.find((u) => u.id === userId);

      // Permitir abrir el modal también para el propio usuario
      this.selectedUserId = userId;
      this.selectedUserName = user ? user.nombre_completo : 'Usuario desconocido';
      this.passwordForm = {
        new_password: '',
        new_password_confirmation: '',
        forzar_cambio_contrasena: true,
      };
      this.showChangePasswordModal = true;
    },
    
    closeChangePasswordModal() {
      this.showChangePasswordModal = false;
    },
    
    toggleShowNewPassword() {
      this.showNewPassword = !this.showNewPassword;
    },
    
    toggleShowConfirmPassword() {
      this.showConfirmPassword = !this.showConfirmPassword;
    },

    setSort(column) {
      if (this.sortBy === column) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortBy = column;
        this.sortDir = 'asc';
      }
    },
    
    async loadUsers() {
      this.loadingUsers = true;
      try {
        const response = await api.get('/users');
 
        // normalizar y añadir marca isSelf
        this.users = response.data.data
          .map((user) => ({
            ...user,
            bloqueado: user.bloqueado === true || user.bloqueado === 1 || user.bloqueado === '1',
          }))
          .map((u) => ({ ...u, isSelf: Number(u.id) === Number(this.currentUserId) }));
 
      } catch (error) {
        console.error('Error al cargar los usuarios:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo cargar la lista de usuarios.'
        });
      } finally {
        this.loadingUsers = false;
      }
    },
    
    openModal(isEditMode, user = null) {
      this.isEditMode = isEditMode;

      if (isEditMode && user) {
        this.form = {
          id: user.id,
          nombre_completo: user.nombre_completo,
          nombre_usuario: user.nombre_usuario,
          email: user.email,
          rol_id: user.rol?.id || '',
          delegacion_id: user.delegacion?.id || '',
          departamento_id: user.departamento?.id || '',
          usuario_responsable_id: user.usuario_responsable?.id || '',
          forzar_cambio_contrasena: user.forzar_cambio_contrasena === true || user.forzar_cambio_contrasena === 1 || user.forzar_cambio_contrasena === '1',
          bloqueado: user.bloqueado === true || user.bloqueado === 1 || user.bloqueado === '1',
        };
      } else {
        this.form = {
          nombre_completo: '',
          nombre_usuario: '',
          email: '',
          password: '',
          confirm_password: '',
          rol_id: null,
          usuario_responsable_id: null,
          forzar_cambio_contrasena: true,
        };
      }

      this.showUserForm = true;
    },
    
    closeModal() {
      const modalElement = document.getElementById('userModal');
      if (modalElement) {
        modalElement.classList.remove('show');
        modalElement.classList.add('d-none');
        modalElement.setAttribute('aria-hidden', 'true');
        modalElement.style.display = 'none';

        const backdrop = document.querySelector('.modal-backdrop');
        if (backdrop) {
          backdrop.parentNode.removeChild(backdrop);
        }

        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
      }

      // Emitir el evento para recargar la tabla
      this.$emit('refreshTable');
    },
    
    async handleSubmit() {
      this.errorMessage = '';
      this.successMessage = '';
      
      // ✅ CONSTRUCCIÓN CORRECTA DEL JSON
      const userData = {
        nombre_completo: this.form.nombre_completo,
        nombre_usuario: this.form.nombre_usuario,
        email: this.form.email,
        rol_id: this.form.rol_id,
        delegacion_id: this.form.delegacion_id,
        departamento_id: this.form.departamento_id,
        usuario_responsable_id: this.form.usuario_responsable_id || null, // ✅ Convertir vacío a null
        forzar_cambio_contrasena: this.form.forzar_cambio_contrasena,
        bloqueado: this.form.bloqueado,
      };

      // ✅ Solo incluir contraseñas en modo CREAR
      if (!this.isEditMode) {
        userData.password = this.form.password;
        userData.password_confirmation = this.form.confirm_password;
      }

      try {
        // ✅ Endpoints corregidos
        const endpoint = this.isEditMode ? `/users/${this.form.id}` : '/users/create';
        const method = this.isEditMode ? 'put' : 'post';
        
        const response = await api[method](endpoint, userData);
        if ([200, 201].includes(response.status)) {
          const successMsg = this.isEditMode
            ? `Usuario "${this.form.nombre_usuario}" actualizado exitosamente`
            : `Usuario "${this.form.nombre_usuario}" creado exitosamente`;
          this.successMessage = successMsg;

          // Mostrar mensaje de éxito temporal (auto-cierra)
          Swal.fire({ 
            icon: 'success', 
            title: successMsg, 
            timer: 2000, 
            showConfirmButton: false 
          });

          // Cerrar el modal
          this.closeModal();

          // Emitir el evento para actualizar la tabla
          this.$emit('refreshTable');
        }
      } catch (error) {
        console.error('Error completo:', error);
        console.error('Datos de respuesta:', error.response?.data);

        if (error.response) {
          if (error.response.status === 422) {
            const backendMessages = error.response.data.messages;
            this.errorMessage = backendMessages ? backendMessages.join(' ') : 'Error de validación';
          } else if (error.response.data.error) {
            this.errorMessage = error.response.data.error;
          } else {
            this.errorMessage = `Error del servidor: ${error.response.status}`;
          }
        } else if (error.request) {
          this.errorMessage = 'No se recibió respuesta del servidor';
        } else {
          this.errorMessage = 'Error al configurar la solicitud';
        }

        // Mensaje de error con botón (no temporizado)
        Swal.fire({ 
          icon: 'error', 
          title: 'Error', 
          text: this.errorMessage 
        });
      }
    },
    
    async deleteUser(userId) {
      const user = this.users.find((u) => u.id === userId);
      if (!user) return;
      
      // ✅ Validación: no permitir eliminarse a sí mismo
      if (this.isSelf(user)) {
        await Swal.fire({
          icon: 'warning',
          title: 'Acción no permitida',
          text: 'No puedes eliminar tu propio usuario.',
          timer: 2000,
          showConfirmButton: false
        });
        return;
      }

      // Pedir motivo obligatorio (textarea) — botón "Eliminar" deshabilitado hasta escribir
      const { value: motivo } = await Swal.fire({
        title: `Eliminar "${user.nombre_completo}"`,
        input: 'textarea',
        inputPlaceholder: 'Introduce el motivo de la eliminación...',
        inputAttributes: { 'aria-label': 'Motivo' },
        showCancelButton: true,
        confirmButtonText: 'Eliminar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#99172A',
        cancelButtonColor: '#6c757d',
        didOpen: () => {
          const textarea = Swal.getInput();
          const confirmBtn = Swal.getConfirmButton();
          confirmBtn.disabled = true;
          textarea.addEventListener('input', () => {
            confirmBtn.disabled = !textarea.value || !textarea.value.trim();
          });
          textarea.focus();
        },
        preConfirm: (val) => {
          if (!val || !val.trim()) {
            Swal.showValidationMessage('El motivo es obligatorio');
          }
          return val;
        }
      });
      if (!motivo) return;

      // Confirmación final mostrando motivo
      const confirmed = await Swal.fire({
        title: 'Confirmar eliminación',
        html: `<strong>Usuario:</strong> ${user.nombre_completo}<br><strong>Motivo:</strong><br>${motivo}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Eliminar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#99172A',
        cancelButtonColor: '#6c757d',
      });
      if (!confirmed.isConfirmed) return;

      try {
        // Llamada al endpoint que marca como eliminado — payload con motivo (requerido)
        const resp = await api.put(`/users/${userId}/mark-deleted`, {
          motivo: motivo.trim()
        });

        if (resp.status === 200 || resp.status === 204) {
          // Actualizar la lista localmente (opcional: recargar desde servidor)
          this.users = this.users.filter((u) => u.id !== userId);

          await Swal.fire({ 
            icon: 'success', 
            title: 'Eliminado', 
            text: `Usuario "${user.nombre_completo}" marcado como eliminado.`, 
            timer: 2000, 
            showConfirmButton: false 
          });
        } else {
          throw new Error('Respuesta inesperada del servidor');
        }
      } catch (err) {
        console.error('Error al marcar usuario como eliminado:', err.response?.data || err.message);
        await Swal.fire({ 
          icon: 'error', 
          title: 'Error', 
          text: err.response?.data?.message || 'No se pudo marcar el usuario como eliminado.' 
        });
      }
    },
    
    async viewLogs(userId) {
      try {
        // Usa solo la ruta relativa, sin baseUrl
        const url = `/logs/user/${userId}`;
        const user = this.users.find((u) => u.id === userId);
        this.selectedUserName = user ? user.nombre_completo : 'Usuario desconocido';

        const response = await api.get(url);
 
        this.userLogs = response.data.logs || [];
        this.filteredLogs = this.userLogs;
        this.showUserLogsModal = true;
      } catch (error) {
        console.error('Error al cargar los logs:', error.response?.data || error.message);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudieron cargar los logs.'
        });
      }
    },

    // Nuevo: ver logs de todos los usuarios
    async viewAllLogs() {
      try {
        const url = `/logs`;
        this.selectedUserName = 'Todos los usuarios';

        const response = await api.get(url);

        // El endpoint puede devolver { logs: [...] } o directamente la lista
        this.userLogs = response.data.logs || response.data || [];
        this.filteredLogs = this.userLogs;
        this.showUserLogsModal = true;
      } catch (error) {
        console.error('Error al cargar los logs de todos los usuarios:', error.response?.data || error.message);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudieron cargar los logs.'
        });
      }
    },

    // Nuevo: ver fichajes de un usuario (muestra en un SweetAlert una lista simple)
    async viewUserFichajes(userId) {
      try {
        const url = `/tickets/fichaje?usuario_id=${userId}`;
        const response = await api.get(url);
        const fichajes = response.data.fichajes || response.data || [];
        this.fichajesForModal = Array.isArray(fichajes) ? fichajes : [];
        this.fichajesModalUserId = userId;
        this.showFichajesModal = true;
      } catch (error) {
        console.error('Error al cargar fichajes:', error.response?.data || error.message);
        Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudieron cargar los fichajes.' });
      }
    },

    // Nuevo: ver todos los fichajes (cabecera) — similar a viewUserFichajes pero sin filtrar por usuario
    async viewAllFichajes() {
      try {
        const url = `/tickets/fichaje`;
        const response = await api.get(url);
        const fichajes = response.data.fichajes || response.data || [];
        this.fichajesForModal = Array.isArray(fichajes) ? fichajes : [];
        this.fichajesModalUserId = null;
        this.showFichajesModal = true;
      } catch (error) {
        console.error('Error al cargar todos los fichajes:', error.response?.data || error.message);
        Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudieron cargar los fichajes.' });
      }
    },

    onFichajesModalVisibleChange(visible) {
      this.showFichajesModal = visible;
      if (!visible) {
        // limpiar el fixed user id cuando se cierra el modal
        this.fichajesModalUserId = null;
      }
    },
    
    closeUserLogsModal() {
      this.showUserLogsModal = false;
      this.filterDate = '';
      this.userLogs = [];
      this.filteredLogs = [];
    },
    
    filterLogsByDate() {
      if (this.filterDate) {
        this.filteredLogs = this.userLogs.filter((log) =>
          log.inicio_sesion.startsWith(this.filterDate)
        );
      } else {
        this.filteredLogs = this.userLogs;
      }
    },
    
    async handleChangePassword() {
      // Validar si es el usuario logueado
      const user = this.users.find((u) => u.id === this.selectedUserId);
      if (this.isSelf(user)) {
        Swal.fire({
          icon: 'warning',
          title: 'Acción no permitida',
          text: 'No puedes cambiar la contraseña de tu propio usuario desde aquí.',
          timer: 2000,
          showConfirmButton: false
        });
        this.closeChangePasswordModal();
        return;
      }
      
      // Validar que las contraseñas coincidan
      if (this.passwordForm.new_password !== this.passwordForm.new_password_confirmation) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Las contraseñas no coinciden.',
          timer: 2000,
          showConfirmButton: false
        });
        return;
      }
      
      // Validar longitud mínima
      if (this.passwordForm.new_password.length < 8) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'La contraseña debe tener al menos 8 caracteres.',
          timer: 2000,
          showConfirmButton: false
        });
        return;
      }
      
      try {
        const response = await api.post(`/users/${this.selectedUserId}/change-password`, {
          new_password: this.passwordForm.new_password,
          new_password_confirmation: this.passwordForm.new_password_confirmation,
          forzar_cambio_contrasena: this.passwordForm.forzar_cambio_contrasena,
        });

        // Mostrar confirmación con SweetAlert2 (temporal)
        await Swal.fire({ 
          icon: 'success', 
          title: 'Contraseña actualizada', 
          text: `Contraseña actualizada correctamente para el usuario ${this.selectedUserName}.`, 
          timer: 2000, 
          showConfirmButton: false 
        });

        // Cerrar el modal
        this.closeChangePasswordModal();
      } catch (err) {
        console.error('Error al cambiar la contraseña:', err.response?.data || err.message);

        // Mostrar error con SweetAlert2
        const errorMessage = err.response?.data?.message || `No se pudo cambiar la contraseña para el usuario ${this.selectedUserName}.`;
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: errorMessage,
          timer: 2500,
          showConfirmButton: false
        });
      }
    },

    // ✅ MÉTODO PARA BLOQUEAR/DESBLOQUEAR
    async toggleUserBlock(user) {
      // ✅ Validación: no permitir bloquearse/desbloquearse a sí mismo
      if (this.isSelf(user)) {
        await Swal.fire({
          icon: 'warning',
          title: 'Acción no permitida',
          text: 'No puedes bloquear/desbloquear tu propio usuario.',
          timer: 2000,
          showConfirmButton: false
        });
        return;
      }
      
      const willBlock = !user.bloqueado;

      // Pedir motivo obligatorio with SweetAlert2 (textarea)
      const { value: motivo } = await Swal.fire({
        title: willBlock ? `Bloquear "${user.nombre_completo}"` : `Desbloquear "${user.nombre_completo}"`,
        input: 'textarea',
        inputPlaceholder: 'Introduce el motivo de la acción...',
        inputAttributes: { 'aria-label': 'Motivo' },
        showCancelButton: true,
        confirmButtonText: willBlock ? 'Bloquear' : 'Desbloquear',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#99172A', // rojo corporativo
        cancelButtonColor: '#6c757d',  // cancelar en gris
        didOpen: () => {
          const textarea = Swal.getInput();
          const confirmBtn = Swal.getConfirmButton();
          // deshabilitar confirm mientras el textarea esté vacío
          confirmBtn.disabled = true;
          textarea.addEventListener('input', () => {
            confirmBtn.disabled = !textarea.value || !textarea.value.trim();
          });
          textarea.focus();
        },
        preConfirm: (val) => {
          if (!val || !val.trim()) {
            Swal.showValidationMessage('El motivo es obligatorio');
          }
          return val;
        }
      });
      if (!motivo) return; // cancelado o validación fallida

      // Confirmación final
      const confirmed = await Swal.fire({
        title: willBlock ? 'Confirmar bloqueo' : 'Confirmar desbloqueo',
        html: `<strong>Usuario:</strong> ${user.nombre_completo}<br><strong>Motivo:</strong><br>${motivo}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: willBlock ? 'Bloquear' : 'Desbloquear',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#99172A',
        cancelButtonColor: '#6c757d',
      });
      if (!confirmed.isConfirmed) return;

      try {
        const resp = await api.put(`/users/${user.id}/toggle-block`, {
          bloqueado: willBlock,
          motivo: motivo.trim()
        });
        if (resp.status === 200) {
          await this.loadUsers();
          Swal.fire({ 
            icon: 'success', 
            title: 'Éxito', 
            text: willBlock ? 'Usuario bloqueado' : 'Usuario desbloqueado', 
            timer: 2000, 
            showConfirmButton: false 
          });
        }
      } catch (err) {
        console.error('Error al cambiar estado del usuario:', err.response?.data || err.message);
        Swal.fire({ 
          icon: 'error', 
          title: 'Error', 
          text: err.response?.data?.message || 'No se pudo completar la operación.' 
        });
      }
    },
    
    // ✅ NUEVO: Cargar usuario actual de forma segura
    async loadCurrentUser() {
      try {
        // Si no hay user_id, intentar obtenerlo desde /me
          if (!this.currentUserId) {
            const meResp = await api.get('/me');
            const meId = meResp?.data?.user?.id ?? meResp?.data?.id ?? null;
            if (meId != null) {
              // usar sessionStorage (consistente con el store y el router)
              sessionStorage.setItem('user_id', String(meId));
            }
          }

          // Verificar rol (usar sessionStorage para mantener consistencia)
          const userRole = Number(sessionStorage.getItem('user_role'));
          if (userRole !== 1) {
            this.$router.replace({ name: 'Welcome' });
            return false;
          }
        
        return true;
      } catch (error) {
        console.warn('Error cargando usuario actual:', error);
        return false;
      }
    },
    
    // ✅ NUEVO: Manejar teclas globales
    handleGlobalKeys(event) {
      // Cerrar modales con Escape
      if (event.key === 'Escape') {
        if (this.showChangePasswordModal) {
          this.closeChangePasswordModal();
        } else if (this.showUserLogsModal) {
          this.closeUserLogsModal();
        }
      }
    },
  },
  async created() {
    // ✅ MEJORADO: Carga más limpia y segura
    const canLoad = await this.loadCurrentUser();
    if (!canLoad) return;
    
    await this.loadUsers();
  },
  mounted() {
    // Añadir event listeners para accesibilidad
    document.addEventListener('keydown', this.handleGlobalKeys);
    // Escuchar eventos desde Header para abrir el modal de cambio de contraseña
    this._onOpenChangePasswordFromHeader = (e) => {
      try {
        const userId = (e && e.detail && e.detail.userId) ? e.detail.userId : null;
        const source = (e && e.detail && e.detail.source) ? e.detail.source : null;
        // If the request comes from the header, ignore it: header will open its own modal.
        if (source === 'header') return;
        this.openChangePasswordModal(userId);
      } catch (err) {}
    };
    try { window.addEventListener('open-change-password', this._onOpenChangePasswordFromHeader); } catch (err) {}
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleGlobalKeys);
    try { window.removeEventListener('open-change-password', this._onOpenChangePasswordFromHeader); } catch (err) {}
  },
  watch: {
    // React to query param used by Header to request opening the change-password modal
    '$route.query.openChangePassword': function (val) {
      if (val) {
        try {
          // If the query includes a source and it's 'header', ignore: header modal will be used.
          const source = this.$route && this.$route.query && this.$route.query.openChangePasswordSource ? String(this.$route.query.openChangePasswordSource) : null;
          if (source === 'header') {
            // just remove the query param marker without opening the view modal
          } else {
            // open modal for current user
            const idFromSession = sessionStorage.getItem('user_id');
            const userId = idFromSession ? Number(idFromSession) : null;
            this.openChangePasswordModal(userId);
          }
        } catch (e) {}
        // remove the query param without reloading
        try {
          const newQuery = Object.assign({}, this.$route.query || {});
          delete newQuery.openChangePassword;
          delete newQuery.openChangePasswordSource;
          this.$router.replace({ name: this.$route.name, query: newQuery }).catch(() => {});
        } catch (e) {}
      }
    }
  },
};
</script>

<style scoped>
/* Unificación de estilos de modales */
.modal-container {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  max-width: 100%;
  max-height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 12000;
  overflow: hidden;
}

.modal-content,
.modal-content-userlogs {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.2);
  width: min(70vw, 900px);
  max-width: 85vw;
  padding: 0;
  animation: fadeIn 0.2s;
  box-sizing: border-box;
  overflow: hidden;
}

.modal-header.red-header {
  background: #99172A;
  color: #fff;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  padding: 18px 24px 12px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0;
  width: 100%;
  box-sizing: border-box;
  margin-top: 0px;
  left: 0px;
}

.modal-header .modal-title {
  font-size: 1.2rem;
  font-weight: 600;
}
.modal-header .user-name {
  font-size: 1rem;
  font-weight: 400;
  color: #ffe6e6;
}
.btn-close {
  background: none;
  border: none;
  font-size: 28px; 
  cursor: pointer;
  color: #ffffff !important;
  width: 50px;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 1 !important;
  text-shadow: 0 1px 2px rgba(0,0,0,0.35);
  font-weight: 700;
  transition: opacity 0.2s;
}
.btn-close:hover {
  opacity: 0.8 !important;
}
.modal-body {
  padding: 24px 24px 12px 24px;
  background: #fff;
  border-radius: 0 0 8px 8px;
  box-sizing: border-box;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 18px 24px 18px 24px;
  background: #fff;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  margin-top: 8px;
}
.logout-button {
  background: #c82333;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 20px;
  font-weight: 500;
  transition: background 0.2s;
}
.logout-button:hover {
  background: #a71d2a;
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
}

.welcome-container {
  display: flex;
  flex-direction: column;
  background-color: #f5f5f5;
  padding: 0 20px;
  box-sizing: border-box;
  min-height: 100vh;
}

.table-container {
  width: 100%;
  overflow-y: auto;
  max-height: calc(100vh * 0.60);
  margin-top: 0;
  margin-bottom: 0;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: white;
}

.title-container {
  margin-bottom: 20px;
  text-align: left;
}

.user-table {
  width: 100%;
  border-collapse: collapse;
}

.user-table th,
.user-table td {
  border-left: none;
  border-right: none;
  border-top: none;
  border-bottom: 1px solid #ddd;
  padding: 12px 8px;
  text-align: left;
}

.user-table th {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: #f5f5f5;
  text-align: center;
  padding: 14px 10px;
  border-bottom: 2px solid #ddd;
  font-weight: 600;
}

.user-table tbody tr:nth-child(even) {
  background-color: #e3f0fb;
}

.user-table tbody tr:nth-child(odd) {
  background-color: #fff;
}

/* fila del usuario logueado */
.user-table tbody tr.self-row {
  /* Quitar resaltado amarillo para el usuario logueado */
  background-color: transparent !important;
  font-weight: normal !important;
}

.user-table tbody tr:hover {
  background-color: #e8f4fd !important;
}

/* Ajustes de ancho para columnas Delegación y Departamento
   - Delegación: columna 5 (index empezando en 1)
   - Departamento: columna 6
   Se fija el ancho en la cabecera y en las celdas, y se aplica truncado con ellipsis.
*/
.user-table th:nth-child(5), .user-table td:nth-child(5) {
  max-width: 180px;
  width: 180px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.user-table th:nth-child(6), .user-table td:nth-child(6) {
  max-width: 220px;
  width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.user-table th:nth-child(7), .user-table td:nth-child(7) {
  max-width: 220px;
  width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Forzar nombre completo en una sola línea (columna 1) */
.user-table th:nth-child(1), .user-table td:nth-child(1) {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 240px;
}

/* Bloqueado: columna 8, ancho fijo y centrar icono */
.user-table th:nth-child(8), .user-table td:nth-child(8) {
  width: 110px;
  max-width: 110px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.user-table td.bloqueado-column .material-icons {
  vertical-align: middle;
  font-size: 20px;
}

.empty-cell {
  color: #999;
  font-style: italic;
}

.self-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 6px;
  font-size: 12px;
  font-weight: 700;
  color: #99172A;
  background: #fff3b0;
  border-radius: 12px;
  vertical-align: middle;
  border: 1px solid rgba(153,23,42,0.08);
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #666;
  gap: 12px;
}

.loading-state .spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #666;
  gap: 12px;
  text-align: center;
}

.empty-state .material-icons {
  font-size: 48px;
  color: #ccc;
}

.action-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #6c757d; /* gris neutro */
  color: #ffffff !important; /* texto blanco */
  width: 30px;
  height: 30px;
  border-radius: 4px;
  font-size: 18px !important;
  line-height: 1;
  margin: 0 6px;
  cursor: pointer;
}

.action-icon:hover {
  background: #5a6268;
  color: #fff !important;
  transform: scale(1.03);
}

.logs-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
  table-layout: fixed;
}

.logs-table th {
  background-color: #f5f5f5;
  text-align: center;
  padding: 10px;
  position: sticky;
  top: 0;
  z-index: 2;
  border-bottom: 2px solid #ddd;
}

.logs-table td {
  padding: 10px;
  border: 1px solid #ddd;
  text-align: center;
}

.login-fallido {
  background-color: #ffe6e6;
  color: #99172A;
}

.expulsado-check {
  color: green;
  font-weight: bold;
}

.fallo {
  color: orange;
  font-weight: bold;
}

.modal-fixed {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 86%;
  max-width: 900px;
  max-height: 90vh;
  overflow: hidden;
  background-color: #fff;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  border-radius: 8px;
}

.logs-container {
  max-height: 60vh;
  overflow-y: auto;
  border: 1px solid #ddd;
}

.bloqueado-column {
  text-align: center;
  vertical-align: middle;
}
.bloqueado-icon {
  color: #99172A !important;
  font-size: 24px !important;
  vertical-align: middle;
}

.btn-primary {
  background-color: #99172A;
  border-color: #99172A;
  color: #fff;
  font-weight: 600;
}

.btn-primary:hover {
  background-color: #7A1222;
  border-color: #7A1222;
}

.btn-add-user {
  background-color: #2e7d32 !important;
  color: #fff !important;
  border: none;
  border-radius: 5px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s ease;
  margin-bottom: 0;
}

.add-user-search-row {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 18px;
}

.btn-add-user:hover {
  background-color: #1b5e20 !important;
}

.password-container {
  position: relative;
}

.password-toggle {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  cursor: pointer;
  color: #99172A;
  padding: 5px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.password-toggle:hover {
  color: #7A1222;
  background-color: #f5f5f5;
}

.password-toggle:focus {
  outline: 2px solid #99172A;
  outline-offset: 2px;
}

.form-check-input {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  border: 2px solid #d0d0d0;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  vertical-align: middle;
  box-sizing: border-box;
  transition: background-color .12s ease, border-color .12s ease, box-shadow .12s ease;
  accent-color: #99172A;
}

.form-check-input:checked {
  background-color: #99172A;
  border-color: #99172A;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='white'><path d='M6.173 10.59L3.5 7.917 2.5 8.917 6.173 12.59 13.5 5.263 12.5 4.263z'/></svg>");
  background-repeat: no-repeat;
  background-position: center;
  background-size: 70% 70%;
}

.form-check-input:focus {
  outline: none;
  box-shadow: 0 0 0 4px rgba(153,23,42,0.12);
  border-color: #99172A;
}

.form-check-label {
  color: #000;
  font-weight: bold;
  margin-left: 5px;
  cursor: pointer;
}

/* ✅ ESTILOS PARA ICONOS */
.icon-row-horizontal {
  display: flex;
  gap: 15px;
  justify-content: center;
  align-items: center;
  padding: 5px;
  /* permitir que el contenedor ocupe toda la altura de la celda */
  height: 100%;
  box-sizing: border-box;
}

/* Forzar que la celda de acciones use el mismo padding/altura que las demás celdas.
   De este modo la línea (border-bottom) queda alineada con el resto de columnas. */
.user-table td.actions {
  padding: 12px 8px; /* mismo padding que otras celdas */
  vertical-align: middle;
  /* asegurar que el contenido (icon-row-horizontal) ocupe toda la altura */
  display: table-cell;
  height: 100%;
  box-sizing: border-box;
}
 
.icon-row-horizontal .material-icons {
   color: #ffffff !important; /* iconos en blanco */
   font-size: 22px !important;
   vertical-align: middle;
   transition: color 0.14s, transform 0.12s, background-color 0.14s;
   cursor: pointer;
   user-select: none;
   padding: 6px; /* área clicable visible */
   border-radius: 6px; /* fondo redondeado */
   background-color: #6c757d !important; /* fondo gris por defecto */
}

.icon-row-horizontal .material-icons:hover {
  color: #ffffff !important; /* mantener blanco al pasar el ratón */
  transform: scale(1.06);
  background-color: #495057 !important; /* gris más oscuro en hover */
}

.icon-row-horizontal .material-icons:focus {
  outline: 2px solid rgba(153,23,42,0.12);
  outline-offset: 2px;
  background-color: #495057 !important;
}

.lock-icon,
.unlock-icon,
.delete-icon {
  color: #ffffff !important; /* iconos de acción en blanco */
}

.lock-icon:hover,
.unlock-icon:hover,
.delete-icon:hover {
  color: #ffffff !important;
}

.icon-row-horizontal .material-icons:active {
  transform: scale(0.95);
}

.actions {
  text-align: center;
  white-space: nowrap;
}

.btn-cancel {
  background-color: #6c757d;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 10px 20px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s ease;
  height: 44px;
  line-height: 24px;
  display: inline-block;
  vertical-align: middle;
}
.btn-cancel:hover {
  background-color: #495057;
}
.btn.btn-primary {
  height: 44px;
  line-height: 24px;
  display: inline-block;
  vertical-align: middle;
}
.form-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}

.btn-export {
  background-color: #2e7d32;
  color: #fff;
}
.btn-export:hover {
  background-color: #1b5e20;
}

.btn-cerrar {
  background-color: #99172A;
  color: #fff;
}
.btn-cerrar:hover {
  background-color: #7A1222;
}

/* Botón para ver logs: fondo rojo corporativo y texto blanco */
.btn-logs {
  background-color: #99172A !important;
  color: #fff !important;
  border: none;
  border-radius: 5px;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.2s ease, box-shadow 0.12s ease;
  margin-bottom: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.btn-logs:hover {
  background-color: #7A1222 !important;
}
.btn-logs:focus {
  outline: none;
  box-shadow: 0 0 0 4px rgba(153,23,42,0.14);
}

/* ✅ ESTILO PARA ACCIONES DESHABILITADAS */
.disabled-action {
  opacity: 0.45;
  cursor: not-allowed !important;
  pointer-events: none !important;
}

.icon-row-horizontal .material-icons.disabled-action {
  color: #9e9e9e !important;
  background-color: transparent !important; /* eliminar fondo cuando está deshabilitado */
}

.icon-row-horizontal .material-icons.disabled-action:hover {
  color: #9e9e9e !important;
  transform: none !important;
  cursor: not-allowed !important;
  background-color: transparent !important;
}

.lock-icon.disabled-action,
.unlock-icon.disabled-action,
.delete-icon.disabled-action {
  color: #9e9e9e !important;
}

/* ✅ RESPONSIVE */
@media (max-width: 768px) {
  .modal-content,
  .modal-content-userlogs {
    width: 95vw;
    max-width: 95vw;
  }
  
  .add-user-search-row {
    flex-direction: column;
    align-items: stretch;
  }
  
  .add-user-search-row input {
    max-width: 100% !important;
  }
  
  .filter-container {
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .icon-row-horizontal {
    flex-wrap: wrap;
    gap: 10px;
  }
  
  .icon-row-horizontal .material-icons {
    font-size: 20px !important;
  }
  
  .user-table th,
  .user-table td {
    padding: 8px 4px;
    font-size: 14px;
  }
  
  .logs-table th,
  .logs-table td {
    padding: 6px 3px;
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .welcome-container {
    padding: 0 10px;
  }
  
  .form-actions {
    flex-direction: column;
    width: 100%;
  }
  
  .btn-cancel,
  .btn.btn-primary {
    width: 100%;
  }
}
</style>